<header class="flex align-items-center gap-3">
  <h3 class="flex-1 m-0 font-medium">
    @if (isCancelMode) {
      <span>{{ 'CONTRACT.CANCEL_CONTRACT_FOR' | translate }}</span>
    } @else if (isCreateMode) {
      <span>{{ 'CONTRACT.NEW_CONTRACT_FOR' | translate }}</span>
    } @else if (isEditMode) {
      <span>{{ 'CONTRACT.UPDATE_CONTRACT_FOR' | translate }}</span>
    }

    <span class="text-primary">{{ data.employee.name }}</span>
  </h3>

  <div class="flex align-items-center">
    @if (canCancelContract) {
      <p-button
        [text]="true"
        [rounded]="true"
        icon="dps-icon dps-icon-delete"
        severity="danger"
        [pTooltip]="'CONTRACT.CANCEL_CONTRACT' | translate"
        tooltipPosition="top"
        (onClick)="enableCancelMode()"
      />
      <p-divider layout="vertical" styleClass="mx-1 min-h-0 h-1rem" />
    }
    <p-button
      [text]="true"
      [plain]="true"
      [rounded]="true"
      icon="dps-icon dps-icon-close"
      (onClick)="dialogRef.close()"
    />
  </div>
</header>

@if (isLoadingContractData()) {
  <div class="flex flex-1 align-items-center justify-content-center">
    <p-progressSpinner />
  </div>
} @else {
  <p-datepicker
    class="align-self-start"
    selectionMode="range"
    appendTo="body"
    rangeSeparator=">"
    inputStyleClass="w-15rem"
    [showIcon]="true"
    [readonlyInput]="true"
    [minDate]="minDate"
    [maxDate]="maxDate"
    [formControl]="datesRangeControl"
  />

  <div class="flex justify-content-between gap-3">
    @if (isCreateMode) {
      @if (wages$ | async; as wages) {
        <div class="flex flex-column flex-auto">
          <p-select
            appendTo="body"
            [options]="wages"
            [formControl]="wageControl"
            [placeholder]="'CONTRACT.EMPLOYEE_WAGE_LABEL' | translate"
          >
            <ng-template pTemplate="selectedItem">
              @if (wageControl.value; as selectedWage) {
                <span>{{ selectedWage.position }}</span>
                <span> - </span>
                <span> {{ selectedWage.statute.name }} </span>
              }
            </ng-template>
            <ng-template let-wage pTemplate="item">
              <span>{{ wage.position }}</span>
              <span> - </span>
              <span> {{ wage.statute.name }} </span>
            </ng-template>
          </p-select>
          @if (wageControl.errors?.['required']) {
            <small class="text-red-500">
              {{ 'CONTRACT.SELECT_EMPLOYEE_WAGE' | translate }}
            </small>
          }
        </div>
      } @else {
        <p-skeleton styleClass="w-15rem h-2rem" />
      }
    } @else {
      <input
        type="text"
        pInputText
        [value]="originalContract.position + ' - ' + originalContract.statute.name"
        [disabled]="true"
        class="flex-auto"
      />
    }
  </div>

  @if (isEditMode || wageControl.value) {
    <dps-address-autocomplete-field
      [label]="'CONTRACT.EMPLOYMENT_ADDRESS_LABEL' | translate"
      [control]="form.controls.employmentAddress"
    />
  }

  @if (isCancelMode) {
    <section class="flex flex-column p-3 gap-3 border-round surface-100">
      <h4 class="m-0 text-center">{{ 'CONTRACT.WHY_CANCEL_CONTRACT' | translate }}</h4>
      @for (item of cancelReasons$ | async; track item.code) {
        <div class="flex align-items-center gap-2">
          <p-radioButton
            [inputId]="item.code"
            [value]="item"
            [formControl]="form.controls.cancelReason"
          />
          <label [for]="item.code">{{ item.name }}</label>
        </div>
      }

      <textarea
        rows="5"
        cols="30"
        pTextarea
        style="resize: none"
        [placeholder]="'CONTRACT.CANCEL_EXTRA_INFO' | translate"
        [formControl]="form.controls.cancelExtraInfo"
      ></textarea>
    </section>
  } @else if (isTabsFormVisible) {
    <p-tabs [value]="1" [lazy]="true" scrollable>
      <p-tablist>
        <p-tab [value]="1">{{ 'CONTRACT.TABS_LABELS.GENERAL' | translate }}</p-tab>
        <p-tab [value]="2">{{ 'CONTRACT.TABS_LABELS.WAGE' | translate }}</p-tab>
        <p-tab [value]="3">{{ 'CONTRACT.TABS_LABELS.INVOICING' | translate }}</p-tab>
        <p-tab [value]="4">{{ 'CONTRACT.TABS_LABELS.OTHER' | translate }}</p-tab>
      </p-tablist>

      <p-tabpanels>
        <p-tabpanel [value]="1">
          <ng-container [ngTemplateOutlet]="scheduleTemplate" />
        </p-tabpanel>

        <p-tabpanel [value]="2">
          <ng-container [ngTemplateOutlet]="wageTabTemplate" />
        </p-tabpanel>

        <p-tabpanel [value]="3">
          <ng-container [ngTemplateOutlet]="invoicingTabTemplate" />
        </p-tabpanel>

        <p-tabpanel [value]="4">
          <ng-container [ngTemplateOutlet]="otherTabTemplate" />
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  } @else {
    <ng-container [ngTemplateOutlet]="scheduleTemplate" />
  }

  <footer class="flex flex-column gap-2 text-center">
    @if (
      selectedScheduleDayForm.controls.toTime.enabled &&
      selectedScheduleDayForm.hasError(lateContractErrorName)
    ) {
      <span class="text-red-500"> {{ 'VALIDATION_ERRORS.LATE_CONTRACT' | translate }}</span>
    } @else if (selectedScheduleDayForm.hasError(maxContractDurationErrorName)) {
      <span class="text-red-500">
        {{
          'VALIDATION_ERRORS.MAX_CONTRACT_DURATION'
            | translate
              : {
                  maxDurationHours: selectedScheduleDayForm.getError(maxContractDurationErrorName)
                    .maxDurationHours,
                }
        }}</span
      >
    }

    @if (form.hasError(extraStatuteMultiDayContractErrorName)) {
      <span class="text-red-500">
        {{ 'VALIDATION_ERRORS.EXTRA_STATUTE_MULTI_DAY_CONTRACT' | translate }}
      </span>
    }

    @if (isPastContractWithConfirmedActual()) {
      <span
        class="text-orange-500 font-medium"
        [innerHTML]="'CONTRACT.CONTRACT_HAS_CONFIRMED_ACTUALS' | translate"
      ></span>
    }

    <dps-field-validation-errors [control]="scheduleFormArray">
      @for (fg of scheduleFormArray.controls; track $index) {
        @if (fg.getError(dimonaRuleError); as error) {
          <small class="text-red-500">
            {{ error.messageKey | translate }}
          </small>
        }
      }
    </dps-field-validation-errors>

    @if (hasDimonaWarning) {
      <small class="text-orange-500">
        {{ 'CONTRACT.DIMONA_RULES_SAVE_CONTRACT' | translate }}
      </small>
    }

    @if (isCancelMode) {
      <p-button
        class="align-self-end"
        [label]="'CONTRACT.CANCEL_CONTRACT' | translate"
        [loading]="isProcessingContract()"
        [disabled]="form.invalid"
        (onClick)="cancelContract()"
      />
    } @else if (isEditMode) {
      <p-button
        class="align-self-end"
        [label]="'CONTRACT.UPDATE_CONTRACT_BUTTON' | translate"
        [loading]="isProcessingContract()"
        [disabled]="form.invalid || (hasLimitedAccess && isPC124SingleDayBlocked)"
        (onClick)="updateContract()"
      />
      @if (hasLimitedAccess && isPC124SingleDayBlocked) {
        <span class="text-red-500">
          {{ 'CONTRACT.PC_124_CONTRACT_DURATION' | translate }}
        </span>
      }
    } @else if (isCreateMode) {
      @if (this.wageControl.value && isFlexStatuteSelected && !isSingleDayContract) {
        <span class="text-red-500">
          {{ 'CONTRACT.FLEX_STATUTES_DAY_CONTRACT_ONLY' | translate }}
        </span>
      }
      @if (isPC124SingleDayBlocked) {
        <span class="text-red-500">
          {{ 'CONTRACT.PC_124_CONTRACT_DURATION' | translate }}
        </span>
      }
      @if (scheduleFormArray.valid && isAllContractSchedulesConfirmed) {
        <p-button
          class="align-self-end"
          [label]="'CONTRACT.CONFIRM_CONTRACT_BUTTON' | translate"
          [loading]="isProcessingContract()"
          [disabled]="
            form.invalid ||
            !isAllContractSchedulesConfirmed ||
            isLoadingWages() ||
            isPC124SingleDayBlocked
          "
          (onClick)="createContract()"
        />
      } @else {
        <span class="text-red-500">
          {{ 'CONTRACT.FILL_IN_ALL_CONTRACT_SCHEDULES' | translate }}
        </span>
      }
    }
  </footer>
}

<ng-template #scheduleTemplate>
  <section class="flex flex-column border-round p-3 surface-100">
    <div class="flex align-items-center justify-content-center gap-2 mb-2">
      <p-button
        text
        plain
        rounded
        icon="dps-icon dps-icon-chevron rotate-180"
        [ngStyle]="{
          visibility: isSingleDayContract || isFirstScheduleDaySelected ? 'hidden' : 'visible',
        }"
        (onClick)="selectScheduleDayIndex(selectedScheduleDayIndex - 1)"
      />
      <span class="font-bold text-center">{{ selectedScheduleFormattedDate }}</span>
      <p-button
        text
        plain
        rounded
        icon="dps-icon dps-icon-chevron"
        [ngStyle]="{
          visibility: isSingleDayContract || isLastScheduleDaySelected ? 'hidden' : 'visible',
        }"
        (onClick)="selectScheduleDayIndex(selectedScheduleDayIndex + 1)"
      />
    </div>

    @if (isCreateMode) {
      <p-selectButton
        [options]="shiftTypeOptions"
        [allowEmpty]="false"
        [unselectable]="true"
        [formControl]="useExistingShiftControl"
        styleClass="w-full"
        class="my-2"
      >
        <ng-template let-shiftType #item>
          <span class="text-sm text-center">{{ shiftType.labelTranslationKey | translate }}</span>
        </ng-template>
      </p-selectButton>

      @if (useExistingShiftControl.value) {
        <p-autoComplete
          styleClass="w-full z-1"
          inputId="shiftNameAutocomplete"
          optionLabel="name"
          appendTo="body"
          dataKey="id"
          [dropdown]="true"
          [showClear]="true"
          [forceSelection]="true"
          [delay]="200"
          [placeholder]="'CONTRACT.SEARCH_SHIFT_PLACEHOLDER' | translate"
          [suggestions]="(shifts$ | async) || []"
          [minLength]="minShiftNameLength"
          [formControl]="shiftAutocompleteControl"
          (completeMethod)="shiftSearchQuery.next($event.query)"
        >
          <ng-template let-shift pTemplate="item">
            <div class="flex justify-content-between align-items-center gap-2 w-full">
              <span>{{ shift.name }}</span>
              <span
                class="z-1 underline hover:no-underline text-red-500"
                (click)="removeShift($event, shift)"
              >
                {{ 'CONTRACT.REMOVE_SHIFT_TEMPLATE' | translate }}
              </span>
            </div>
          </ng-template>
        </p-autoComplete>
        @if (shiftAutocompleteControl.invalid) {
          <small class="text-red-500">
            {{ 'CONTRACT.SELECT_SHIFT' | translate }}
          </small>
        }
      } @else if (selectedScheduleDayForm.controls.createShiftTemplate.value) {
        <input
          type="text"
          pInputText
          class="z-1"
          [formControl]="selectedScheduleDayForm.controls.shiftTemplateName"
          [placeholder]="'CONTRACT.NEW_SHIFT_INPUT_PLACEHOLDER' | translate"
        />
      }
      @if (
        selectedScheduleDayForm.controls.shiftTemplateName.hasError('required') &&
        selectedScheduleDayForm.controls.createShiftTemplate.value
      ) {
        <small class="text-red-500 mb-1">
          {{ 'CONTRACT.GIVE_SHIFT_NAME' | translate }}
        </small>
      }
    }

    @if (isDayScheduleSectionShown) {
      <section
        class="flex flex-column gap-2 transition-all transition-duration-200 relative"
        [ngClass]="{
          'surface-200 p-2 pt-3 -mt-1 border-round-bottom':
            isCreateMode &&
            (useExistingShiftControl.value ||
              selectedScheduleDayForm.controls.createShiftTemplate.value),
          'can-copy-schedule': canCopySchedule,
        }"
      >
        <div class="grid grid-nogutter align-items-center">
          <div class="col"></div>
          <div class="col-3 text-center">{{ 'CONTRACT.SCHEDULE_FROM' | translate }}</div>
          <div class="col-1"></div>
          <div class="col-3 text-center">{{ 'CONTRACT.SCHEDULE_UNTIL' | translate }}</div>
        </div>

        <div class="grid grid-nogutter align-items-center">
          <div class="col">{{ 'CONTRACT.SCHEDULE_WORK_HOURS' | translate }}</div>
          <div class="col-3 flex justify-content-center">
            <dps-time-field [control]="selectedScheduleDayForm.controls.fromTime" />
          </div>
          <div class="col-1 flex justify-content-center">
            <span class="dps-icon dps-icon-chevron"></span>
          </div>
          <div class="col-3 flex justify-content-center">
            <dps-time-field [control]="selectedScheduleDayForm.controls.toTime" />
          </div>
        </div>

        <div class="grid grid-nogutter align-items-center">
          <div class="col">{{ 'CONTRACT.SCHEDULE_PAUSE_HOURS' | translate }}</div>
          <div class="col-3 flex justify-content-center">
            <dps-time-field [control]="selectedScheduleDayForm.controls.pauseFromTime" />
          </div>
          <div class="col-1 flex justify-content-center">
            <span class="dps-icon dps-icon-chevron"></span>
          </div>
          <div class="col-3 flex justify-content-center">
            <dps-time-field [control]="selectedScheduleDayForm.controls.pauseToTime" />
          </div>
        </div>

        <p-button
          class="copy-schedule"
          appendTo="body"
          [icon]="isScheduleCopied() ? 'dps-icon dps-icon-check' : 'dps-icon dps-icon-copy'"
          [text]="true"
          [plain]="true"
          [rounded]="true"
          [pTooltip]="'CONTRACT.COPY_SCHEDULE_TOOLTIP' | translate"
          (onClick)="copySchedule()"
        />
      </section>
    }

    @if (isCreateMode && !useExistingShiftControl.value) {
      <div class="flex align-items-center gap-2 pt-3">
        <p-checkbox
          [binary]="true"
          [formControl]="selectedScheduleDayForm.controls.createShiftTemplate"
          inputId="createShiftTemplate-{{ selectedScheduleDayIndex }}"
          (onChange)="selectedScheduleDayForm.controls.createShiftTemplate.setValue($event.checked)"
        />
        <label for="createShiftTemplate-{{ selectedScheduleDayIndex }}">
          {{ 'CONTRACT.CREATE_SHIFT_TEMPLATE_CHECKBOX' | translate }}
        </label>
      </div>
    }
  </section>

  @if (!isSelectedScheduleDayHasMinWorkDuration) {
    <span class="text-center text-red-500">
      {{ 'VALIDATION_ERRORS.MIN_CONTRACT_WORK_DURATION_PER_PC' | translate }}
    </span>
  }
</ng-template>

<ng-template #wageTabTemplate>
  <div class="flex flex-column gap-3">
    <div class="flex flex-column">
      <label class="mb-1" for="hourlyWage">
        {{ 'EMPLOYEE_WAGE.GROSS_HOURLY_WAGE_LABEL' | translate }}
      </label>
      <p-inputNumber
        inputId="hourlyWage"
        mode="currency"
        currency="EUR"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="form.controls.wageHour"
      />
      <dps-field-validation-errors [control]="form.controls.wageHour">
        @if (form.controls.wageHour.errors?.['min']; as minErrorObj) {
          <small class="text-red-500">{{
            'VALIDATION_ERRORS.EMPLOYEE_WAGE_MIN'
              | translate
                : {
                    min: minErrorObj.min | currency,
                  }
          }}</small>
        }
        @if (form.controls.wageHour.errors?.['max']; as maxErrorObj) {
          <small class="text-red-500">{{
            'VALIDATION_ERRORS.EMPLOYEE_WAGE_MAX'
              | translate
                : {
                    max: maxErrorObj.max | currency,
                  }
          }}</small>
        }
      </dps-field-validation-errors>
    </div>
    <dps-toggle-card
      [title]="'EMPLOYEE_WAGE.TRANSPORTATION_ALLOWANCE_LABEL' | translate"
      [toggled]="travelAllowanceForm.controls.isEnabled.value"
      (toggleChange)="travelAllowanceForm.controls.isEnabled.setValue($event)"
    >
      <div class="flex flex-column gap-3">
        <div class="flex flex-column">
          <label class="mb-1" for="transportationType">
            {{ 'EMPLOYEE_WAGE.TRANSPORTATION_TYPE_LABEL' | translate }}
          </label>
          <p-select
            [options]="(travelAllowances$ | async) || []"
            [optionLabel]="dictionaryItemOptionLabel"
            [dataKey]="dictionaryItemOptionValue"
            [autoOptionFocus]="false"
            inputId="transportationType"
            styleClass="w-full"
            appendTo="body"
            [formControl]="travelAllowanceForm.controls.travelAllowance"
          />
          <dps-field-validation-errors [control]="travelAllowanceForm.controls.travelAllowance" />
        </div>
        <div class="flex flex-column">
          <label class="mb-1" for="transportationDistance">
            {{ 'EMPLOYEE_WAGE.TRANSPORTATION_DISTANCE_LABEL' | translate }}
          </label>
          <p-inputNumber
            inputId="transportationDistance"
            styleClass="w-full"
            mode="decimal"
            [maxFractionDigits]="4"
            [formControl]="travelAllowanceForm.controls.distanceKm"
          />
          <dps-field-validation-errors [control]="travelAllowanceForm.controls.distanceKm" />
        </div>
        <div class="flex flex-column">
          <label class="mb-1" for="transportationAmount">
            {{ 'EMPLOYEE_WAGE.TRANSPORTATION_AMOUNT_LABEL' | translate }}
          </label>
          <p-inputNumber
            inputId="transportationAmount"
            mode="currency"
            currency="EUR"
            [minFractionDigits]="4"
            styleClass="w-full"
            [formControl]="travelAllowanceForm.controls.forfait"
          />
          <dps-field-validation-errors [control]="travelAllowanceForm.controls.forfait" />
        </div>
      </div>
    </dps-toggle-card>

    <dps-toggle-card
      [title]="'EMPLOYEE_WAGE.MEAL_VOUCHERS_LABEL' | translate"
      [toggled]="mealVoucherForm.controls.isEnabled.value"
      (toggleChange)="mealVoucherForm.controls.isEnabled.setValue($event)"
    >
      <div class="flex flex-column gap-3">
        <div class="flex flex-column">
          <label class="mb-1" for="mealVouchersTotal">
            {{ 'EMPLOYEE_WAGE.MEAL_VOUCHERS_TOTAL_LABEL' | translate }}
          </label>
          <p-inputNumber
            inputId="mealVouchersTotal"
            mode="currency"
            currency="EUR"
            [minFractionDigits]="2"
            styleClass="w-full"
            [formControl]="mealVoucherForm.controls.shareTotal"
          />
          <dps-field-validation-errors [control]="mealVoucherForm.controls.shareTotal">
            @if (mealVoucherForm.controls.shareTotal.errors?.['min']; as minErrorObj) {
              <small class="text-red-500">{{
                'VALIDATION_ERRORS.MEAL_VOUCHERS_TOTAL_MIN'
                  | translate
                    : {
                        min: minErrorObj.min | currency,
                      }
              }}</small>
            }
            @if (mealVoucherForm.controls.shareTotal.errors?.['max']; as maxErrorObj) {
              <small class="text-red-500">{{
                'VALIDATION_ERRORS.MEAL_VOUCHERS_TOTAL_MAX'
                  | translate
                    : {
                        max: maxErrorObj.max | currency,
                      }
              }}</small>
            }
          </dps-field-validation-errors>
        </div>
        <div class="flex flex-column">
          <label class="mb-1" for="firstName">
            {{ 'EMPLOYEE_WAGE.MEAL_VOUCHERS_EMPLOYER_SHARE_LABEL' | translate }}
          </label>
          <p-inputNumber
            inputId="transportationAmount"
            mode="currency"
            currency="EUR"
            [minFractionDigits]="2"
            styleClass="w-full"
            [formControl]="mealVoucherForm.controls.shareCompany"
          />
          <dps-field-validation-errors [control]="mealVoucherForm.controls.shareCompany" />
        </div>
        <div class="flex flex-column">
          <label class="mb-1" for="firstName">
            {{ 'EMPLOYEE_WAGE.MEAL_VOUCHERS_EMPLOYEE_SHARE_LABEL' | translate }}
          </label>
          <p-inputNumber
            inputId="transportationAmount"
            mode="currency"
            currency="EUR"
            [minFractionDigits]="2"
            styleClass="w-full"
            [formControl]="mealVoucherForm.controls.shareEmployee"
          />
          <dps-field-validation-errors [control]="mealVoucherForm.controls.shareEmployee">
            @if (mealVoucherForm.controls.shareEmployee.errors?.['min']; as minErrorObj) {
              <small class="text-red-500">
                {{
                  'VALIDATION_ERRORS.MEAL_VOUCHERS_EMPLOYEE_MIN'
                    | translate
                      : {
                          min: minErrorObj.min | currency,
                        }
                }}
              </small>
            }
          </dps-field-validation-errors>
        </div>
        <div class="flex flex-column">
          <label class="mb-1" for="mvMinHours">
            {{ 'EMPLOYEE_WAGE.MEAL_VOUCHERS_MIN_WORK_HOURS_LABEL' | translate }}
          </label>
          <p-inputNumber
            minFractionDigits="1"
            maxFractionDigits="1"
            inputId="mvMinHours"
            styleClass="w-full"
            [formControl]="mealVoucherForm.controls.minimumHours"
          />
          <dps-field-validation-errors [control]="mealVoucherForm.controls.minimumHours" />
        </div>
      </div>
    </dps-toggle-card>

    <div class="flex justify-content-between align-items-center">
      <label for="ecoVouchers">{{ 'EMPLOYEE_WAGE.ECO_VOUCHERS_LABEL' | translate }}</label>
      <p-toggleswitch inputId="ecoVouchers" [formControl]="form.controls.invoiceEcoWeekly" />
    </div>
  </div>
</ng-template>

<ng-template #invoicingTabTemplate>
  <div class="flex flex-column gap-3">
    <div class="flex flex-column">
      <label class="mb-1" for="coefficient">
        {{ 'CONTRACT.INVOICING.COEFFICIENT' | translate }}
      </label>
      <p-inputNumber
        inputId="coefficient"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="invoicingForm.controls.coefficient"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.coefficient">
        @if (
          invoicingForm.controls.coefficient.errors?.['min'] ||
          invoicingForm.controls.coefficient.errors?.['max']
        ) {
          <small class="text-red-500">
            {{
              'COEFFICIENTS.MIN_MAX_VALIDATION'
                | translate: { fieldLabel: 'CONTRACT.INVOICING.COEFFICIENT' | translate }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="coefficientBankHoliday">
        {{ 'CONTRACT.INVOICING.COEFFICIENT_BANK_HOLIDAY' | translate }}
      </label>
      <p-inputNumber
        inputId="coefficientBankHoliday"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="invoicingForm.controls.coefficientBankHoliday"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.coefficientBankHoliday">
        @if (
          invoicingForm.controls.coefficientBankHoliday.errors?.['min'] ||
          invoicingForm.controls.coefficientBankHoliday.errors?.['max']
        ) {
          <small class="text-red-500">
            {{
              'COEFFICIENTS.MIN_MAX_VALIDATION'
                | translate
                  : { fieldLabel: 'CONTRACT.INVOICING.COEFFICIENT_BANK_HOLIDAY' | translate }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="coefficientTravelAllowance">
        {{ 'CONTRACT.INVOICING.COEFFICIENT_TRANSPORTATION_ALLOWANCE' | translate }}
      </label>
      <p-inputNumber
        inputId="coefficientTravelAllowance"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="invoicingForm.controls.coefficientTravelAllowance"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.coefficientTravelAllowance">
        @if (
          invoicingForm.controls.coefficientTravelAllowance.errors?.['min'] ||
          invoicingForm.controls.coefficientTravelAllowance.errors?.['max']
        ) {
          <small class="text-red-500">
            {{
              'COEFFICIENTS.MIN_MAX_VALIDATION'
                | translate
                  : {
                      fieldLabel:
                        'CONTRACT.INVOICING.COEFFICIENT_TRANSPORTATION_ALLOWANCE' | translate,
                    }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="coefficientMealVouchers">
        {{ 'CONTRACT.INVOICING.COEFFICIENT_MEALVOUCHERS' | translate }}
      </label>
      <p-inputNumber
        inputId="coefficientMealVouchers"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="invoicingForm.controls.coefficientMealVouchers"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.coefficientMealVouchers">
        @if (
          invoicingForm.controls.coefficientMealVouchers.errors?.['min'] ||
          invoicingForm.controls.coefficientMealVouchers.errors?.['max']
        ) {
          <small class="text-red-500">
            {{
              'COEFFICIENTS.MIN_MAX_VALIDATION'
                | translate
                  : { fieldLabel: 'CONTRACT.INVOICING.COEFFICIENT_MEALVOUCHERS' | translate }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="coefficientEcoVouchers">
        {{ 'CONTRACT.INVOICING.COEFFICIENT_ECOVOUCHERS' | translate }}
      </label>
      <p-inputNumber
        inputId="coefficientEcoVouchers"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="invoicingForm.controls.coefficientEcoVouchers"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.coefficientEcoVouchers">
        @if (
          invoicingForm.controls.coefficientEcoVouchers.errors?.['min'] ||
          invoicingForm.controls.coefficientEcoVouchers.errors?.['max']
        ) {
          <small class="text-red-500">
            {{
              'COEFFICIENTS.MIN_MAX_VALIDATION'
                | translate
                  : { fieldLabel: 'CONTRACT.INVOICING.COEFFICIENT_ECOVOUCHERS' | translate }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="dimonaCost">
        {{ 'CONTRACT.INVOICING.DIMONA_COST' | translate }}
      </label>
      <p-inputNumber
        inputId="dimonaCost"
        [minFractionDigits]="4"
        styleClass="w-full"
        [formControl]="invoicingForm.controls.dimonaCost"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.dimonaCost">
        @if (
          invoicingForm.controls.dimonaCost.errors?.['min'] ||
          invoicingForm.controls.dimonaCost.errors?.['max']
        ) {
          <small class="text-red-500">
            {{
              'COEFFICIENTS.MIN_MAX_VALIDATION'
                | translate: { fieldLabel: 'CONTRACT.INVOICING.DIMONA_COST' | translate }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="defaultTaxRate">
        {{ 'CONTRACT.INVOICING.STANDARD_TAX_TARIFF' | translate }}
      </label>
      <p-select
        inputId="defaultTaxRate"
        styleClass="w-full"
        [options]="(defaultTaxRates$ | async) || []"
        [optionLabel]="dictionaryItemOptionLabel"
        [formControl]="invoicingForm.controls.defaultTaxRate"
      />
      <dps-field-validation-errors [control]="invoicingForm.controls.defaultTaxRate" />
    </div>
  </div>
</ng-template>

<ng-template #otherTabTemplate>
  <div class="flex flex-column gap-3">
    <div class="flex flex-column">
      <label class="mb-1" for="reason">
        {{ 'CONTRACT.EMPLOYMENT_REASON' | translate }}
      </label>
      <p-select
        inputId="reason"
        appendTo="body"
        styleClass="w-full"
        [options]="(reasons$ | async) || []"
        [optionLabel]="dictionaryItemOptionLabel"
        [formControl]="form.controls.reason"
      />
      <dps-field-validation-errors [control]="form.controls.reason" />
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="compensationHours">
        {{ 'CONTRACT.COMPENSATION_HOURS' | translate }}
      </label>
      <p-select
        inputId="compensationHours"
        appendTo="body"
        styleClass="w-full"
        [options]="(compensationHours$ | async) || []"
        [optionLabel]="dictionaryItemOptionLabel"
        [formControl]="form.controls.compensationHours"
      />
      <dps-field-validation-errors [control]="form.controls.compensationHours" />
    </div>
    <div class="flex flex-column">
      <h4 class="mt-0 mb-3 text-primary">{{ 'CONTRACT.INTERN_HEADER' | translate }}</h4>
      <div class="flex flex-column">
        <label class="mb-1" for="revenueConsultant">
          {{ 'CONTRACT.DEFAULT_REVENUE_SALES_REP' | translate }}
        </label>
        <p-select
          [options]="(consultants$ | async) || []"
          dataKey="id"
          [autoOptionFocus]="false"
          inputId="revenueConsultant"
          styleClass="w-full"
          appendTo="body"
          [formControl]="form.controls.revenueConsultant"
        >
          <ng-template pTemplate="selectedItem">
            <ng-container
              [ngTemplateOutlet]="revenueConsultantOptionTemplate"
              [ngTemplateOutletContext]="{ revConsultant: form.controls.revenueConsultant.value }"
            />
          </ng-template>

          <ng-template let-revConsultant pTemplate="item">
            <ng-container
              [ngTemplateOutlet]="revenueConsultantOptionTemplate"
              [ngTemplateOutletContext]="{ revConsultant }"
            />
          </ng-template>
        </p-select>
        <dps-field-validation-errors [control]="form.controls.revenueConsultant" />
      </div>
    </div>
    <div class="flex flex-column">
      <h4 class="mt-0 mb-3 text-primary">{{ 'CONTRACT.WORKSCHEDULE_HEADER' | translate }}</h4>
      <label class="mb-1" for="employeeHourPerWeek">
        {{ 'CONTRACT.EMPLOYEE_HOURS_WEEKLY' | translate }}
      </label>
      <p-inputNumber
        inputId="employeeHourPerWeek"
        styleClass="w-full"
        [minFractionDigits]="2"
        [suffix]="'CONTRACT.HOURS_WEEKLY_SUFFIX' | translate"
        [formControl]="form.controls.employeeHoursPerWeek"
      />
      <dps-field-validation-errors [control]="form.controls.employeeHoursPerWeek" />
    </div>
    <div class="flex flex-column">
      <label class="mb-1" for="companyHoursPerWeek">
        {{ 'CONTRACT.COMPANY_HOURS_WEEKLY' | translate }}
      </label>
      <p-inputNumber
        inputId="companyHoursPerWeek"
        styleClass="w-full"
        [minFractionDigits]="2"
        [suffix]="'CONTRACT.HOURS_WEEKLY_SUFFIX' | translate"
        [formControl]="form.controls.companyHoursPerWeek"
      />
      <dps-field-validation-errors [control]="form.controls.companyHoursPerWeek">
        @if (form.controls.companyHoursPerWeek.hasError('max')) {
          <small class="text-red-500">
            {{
              'VALIDATION_ERRORS.COMPANY_WORK_SCHEDULE_MAX'
                | translate
                  : {
                      maxHours: companyHoursPerWeekMax,
                    }
            }}
          </small>
        }
      </dps-field-validation-errors>
    </div>
  </div>
</ng-template>

<ng-template let-revConsultant="revConsultant" #revenueConsultantOptionTemplate>
  @if (revConsultant) {
    <span>{{ revConsultant.firstName }}</span>
    <span>&nbsp;</span>
    <span>{{ revConsultant.lastName }}</span>
  }
</ng-template>

<p-confirmdialog class="absolute" />
